import moment from 'moment';

import { LinkedInstanceChartDataDto, ThresholdsDto } from '@pimm/services/lib/store-equipment';
import { createMetricsets, FIVE_MIN_MS, getThresholdOffset } from '../_helper';
import { ThresholdColors } from '../consts';

// Static SVG line chart for the print report. The live chart uses
// Victory + onLayout/ResizeObserver for responsive sizing, which doesn't
// fire during window.print() — so the printed PDF loses the data line,
// X-axis, and signal/door bars. This component renders pure SVG with
// fixed coordinates so the print engine has nothing to re-measure.
//
// Element parity with the live chart:
//   - Y-axis tick labels (6 ticks)
//   - Red threshold reference lines + pill labels at the right edge
//   - Temperature data line (single path, solid blue)
//   - X-axis time labels (4-5 ticks across the range)
//   - Signal bar row (teal online / pale offline)
//   - Door bar row (fuchsia open / pale closed) — only when door data exists

type Props = {
  linkedInstances?: LinkedInstanceChartDataDto[];
  thresholds?: ThresholdsDto;
  startDate: Date;
  endDate: Date;
  width?: number;
  height?: number;
};

const SIGNAL_ONLINE = '#14B8A6';  // teal.500
const SIGNAL_OFFLINE = '#99F6E4'; // teal.200
const DOOR_CLOSED = '#F0ABFC';    // fuchsia.300
const DOOR_OPEN = '#D946EF';      // fuchsia.500
const AXIS_TEXT = '#6b7280';
const GRID = '#e5e7eb';
const BAR_BORDER = '#4b5563'; // gray.600

export const PrintTemperatureChart = ({ linkedInstances, thresholds, startDate, endDate, width = 700, height = 260 }: Props) => {
  const linkedTemp = linkedInstances?.find(g => !!g?.Temperature);
  const linkedDoor = linkedInstances?.find(g => !!g?.Door);

  const startMs = startDate.getTime();
  const endMs = endDate.getTime();
  const range = Math.max(1, endMs - startMs);

  // Include all data (no range pre-filter) — same as the live chart which passes
  // everything to Victory and relies on domain clipping. Strict pre-filtering would
  // drop points just outside the range boundary, causing the line to start late or
  // leave a gap near the edge. A clipPath below clips the rendered path instead.
  const tempData = (linkedTemp?.Temperature?.Data ?? []).filter((p: any) => !!p?.Timestamp && typeof p.Value === 'number');

  // Use createMetricsets without range pre-filtering — same as live chart.
  // Segments outside [startMs, endMs] are clipped at render time.
  const signalDatasets = createMetricsets(linkedTemp?.RealTimeStatus?.Data);
  const doorDatasets = createMetricsets(linkedDoor?.Door?.Data, FIVE_MIN_MS);
  const hasDoor = !!linkedDoor;
  const hasSignal = signalDatasets.length > 0;

  // Padding inside the SVG.
  const padL = 34;
  const padR = 46;
  const padT = 8;
  const signalBarH = hasSignal ? 8 : 0;
  const doorBarH = hasDoor ? 8 : 0;
  const xAxisH = 18;
  const barGap = hasSignal && hasDoor ? 2 : 0;
  const barBottomPad = hasSignal || hasDoor ? 4 : 0;
  const padB = xAxisH + signalBarH + barGap + doorBarH + barBottomPad;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  // Y-axis bounds — mirrors live chart: use API-precomputed statistics and expand
  // to include red thresholds, then add ±1 padding.
  const statistics = linkedTemp?.Temperature?.Statistics;
  const dataMax = Math.round(statistics?.Max ?? 0);
  const dataMin = Math.round(statistics?.Min ?? 0);
  const upperRedTick = typeof thresholds?.UpperRed === 'number' ? Math.round(thresholds.UpperRed) : undefined;
  const lowerRedTick = typeof thresholds?.LowerRed === 'number' ? Math.round(thresholds.LowerRed) : undefined;
  const yMax = Math.max(dataMax, upperRedTick ?? dataMax) + 1;
  const yMin = Math.min(dataMin, lowerRedTick ?? dataMin) - 1;
  const ySpan = Math.max(1, yMax - yMin);

  const xFor = (ms: number) => padL + ((ms - startMs) / range) * innerW;
  const yFor = (v: number) => padT + ((yMax - v) / ySpan) * innerH;

  // Gradient stops — mirrors the live chart's gradient array exactly.
  // getThresholdOffset(value, min, max) = (max - value) / (max - min).
  const g = (t: number) => getThresholdOffset(t, yMin, yMax);
  const gradientStops = [
    { offset: 0, color: ThresholdColors.Red },
    { offset: g(thresholds?.UpperRed ?? 0), color: ThresholdColors.Red },
    { offset: g(thresholds?.UpperRed ?? 0), color: ThresholdColors.Yellow },
    { offset: g(thresholds?.UpperYellow ?? 0), color: ThresholdColors.Yellow },
    { offset: g(thresholds?.UpperYellow ?? 0), color: ThresholdColors.Green },
    { offset: g(thresholds?.UpperGreen ?? 0), color: ThresholdColors.Green },
    { offset: g(thresholds?.UpperGreen ?? 0), color: ThresholdColors.Blue },
    { offset: g(thresholds?.LowerGreen ?? 0), color: ThresholdColors.Blue },
    { offset: g(thresholds?.LowerGreen ?? 0), color: ThresholdColors.Green },
    { offset: g(thresholds?.LowerYellow ?? 0), color: ThresholdColors.Green },
    { offset: g(thresholds?.LowerYellow ?? 0), color: ThresholdColors.Yellow },
    { offset: g(thresholds?.LowerRed ?? 0), color: ThresholdColors.Yellow },
    { offset: g(thresholds?.LowerRed ?? 0), color: ThresholdColors.Red },
    { offset: 1, color: ThresholdColors.Red },
  ];

  // Stable IDs — include innerH + innerW so multiple charts on the same print page
  // don't collide on gradient or clipPath defs.
  const gradientId = `temp-grad-${thresholds?.UpperRed ?? 'x'}-${thresholds?.LowerRed ?? 'x'}-${innerH}`;
  const clipId = `temp-clip-${innerW}-${innerH}`;

  // Y-axis ticks — 6 evenly spaced integer values across the range (matches live chart).
  const yTickCount = 6;
  const yTicks: number[] = [];
  const yTickInterval = (yMax - yMin) / (yTickCount - 1);
  for (let i = 0; i < yTickCount; i++) {
    yTicks.push(Math.round(yMin + i * yTickInterval));
  }

  // X-axis ticks — mirrors live chart tick placement and label format exactly.
  // Uses integer hours (moment().diff floor) and rounded days matching TemperatureChart.
  const totalHours = Math.floor(range / (60 * 60 * 1000));
  const days = Math.round(totalHours / 24);
  const xTicks: number[] = [];

  if (totalHours < 6) {
    let t = startMs;
    while (t <= endMs) {
      xTicks.push(t);
      t += 30 * 60 * 1000;
    }
  } else if (days <= 1) {
    for (let i = 0; i < 5; i++) {
      xTicks.push(startMs + i * 6 * 60 * 60 * 1000);
    }
  } else if (days >= 90) {
    xTicks.push(startMs);
    for (let i = 1; i <= 9; i++) {
      xTicks.push(moment(startMs).add(i * 10, 'days').valueOf());
    }
  } else if (days >= 30) {
    xTicks.push(startMs);
    xTicks.push(moment(startMs).add(7, 'days').valueOf());
    xTicks.push(moment(startMs).add(15, 'days').valueOf());
    xTicks.push(moment(startMs).add(22, 'days').valueOf());
    xTicks.push(moment(startMs).add(30, 'days').valueOf());
  } else {
    for (let d = 0; d <= days; d++) {
      xTicks.push(moment(startMs).add(d, 'days').valueOf());
    }
  }

  // For the 24-hour view Victory renders all 5 ticks including the midnight end-of-day tick
  // even when it falls 5 minutes past the range end (e.g. range ends at 11:55 PM).
  // All other range sizes filter strictly to [startMs, endMs].
  const visibleXTicks =
    totalHours >= 6 && days <= 1 ? xTicks : xTicks.filter(t => t >= startMs && t <= endMs);

  const xLabelFn = (tick: number, i: number): string => {
    if (totalHours < 6) return moment(tick).format(i === 0 ? 'MMM DD' : 'h:mm A');
    if (days <= 1) return moment(tick).format(i === 0 || i === 4 ? 'MMM DD' : 'hh A');
    return moment(tick).format('MMM DD');
  };

  // Data path with null breaks for gaps > 5 min — matches live chart gap handling.
  // Single isolated points (no neighbours within 5 min) use "M x,y L x,y" so the
  // round linecap renders a visible dot — plain "M x,y" produces nothing in SVG.
  const GAP_BREAK_MS = 5 * 60 * 1000;
  const segments: string[] = [];
  let current = '';

  for (let i = 0; i < tempData.length; i++) {
    const p = tempData[i] as any;
    const t = new Date(p.Timestamp).getTime();
    const x = xFor(t);
    const y = yFor(p.Value);
    const nextT = i < tempData.length - 1 ? new Date((tempData[i + 1] as any).Timestamp).getTime() : null;
    const prevT = i > 0 ? new Date((tempData[i - 1] as any).Timestamp).getTime() : null;
    const gapBefore = prevT === null || t - prevT > GAP_BREAK_MS;
    const gapAfter = nextT === null || nextT - t > GAP_BREAK_MS;

    if (gapBefore) {
      if (current) segments.push(current);
      current = `M ${x},${y}`;
    } else {
      current += ` L ${x},${y}`;
    }

    // Isolated single point — add a zero-length L so round linecap renders a dot.
    if (gapBefore && gapAfter) {
      current += ` L ${x},${y}`;
    }
  }
  if (current) segments.push(current);
  const dataPath = segments.join(' ');

  // Bar row Y positions — below the x-axis.
  const signalY = padT + innerH + xAxisH;
  const doorY = signalY + signalBarH + barGap;

  // Mirrors live chart renderBar: clips each createMetricsets segment to [startMs, endMs].
  // fillRange=true: first segment starts at startMs, last ends at endMs (same as live chart).
  const renderBarRects = (
    datasets: ReturnType<typeof createMetricsets>,
    colorFor: (v: number) => string,
    y: number,
    h: number,
    fillRange: boolean,
    keyPrefix: string,
  ) => {
    return datasets.map((seg, index) => {
      const isFirst = index === 0;
      const isLast = index === datasets.length - 1;
      const segStart = seg.startTime ?? startMs;
      const segEnd = seg.endTime ?? endMs;
      if (segEnd <= startMs || segStart >= endMs) return null;
      const x1 = xFor(fillRange && isFirst ? startMs : Math.max(segStart, startMs));
      const x2 = xFor(fillRange && isLast ? endMs : Math.min(segEnd, endMs));
      if (x2 <= x1) return null;
      return <rect key={`${keyPrefix}-${index}`} x={x1} y={y} width={Math.max(0.5, x2 - x1)} height={h} fill={colorFor(seg.value)} />;
    });
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', fontFamily: 'Inter, sans-serif' }}
    >
      {/* Vertical gradient that colors the data line by threshold band. */}
      <defs>
        <linearGradient id={gradientId} x1={0} y1={padT} x2={0} y2={padT + innerH} gradientUnits="userSpaceOnUse">
          {gradientStops.map((s, i) => (
            <stop key={i} offset={s.offset} stopColor={s.color} />
          ))}
        </linearGradient>
        {/* Clips the data line so off-range points (pre-startMs / post-endMs) don't bleed outside the chart area */}
        <clipPath id={clipId}>
          <rect x={padL} y={padT} width={innerW} height={innerH} />
        </clipPath>
      </defs>

      {/* Background grid — light horizontal lines at each Y tick */}
      {yTicks.map((tick, i) => (
        <line key={`yg-${i}`} x1={padL} y1={yFor(tick)} x2={padL + innerW} y2={yFor(tick)} stroke={GRID} strokeWidth={0.5} />
      ))}

      {/* X-axis baseline — mirrors VictoryAxis stroke: colors.gray[200], strokeWidth: 0.5 */}
      <line x1={padL} y1={padT + innerH} x2={padL + innerW} y2={padT + innerH} stroke={GRID} strokeWidth={0.5} />

      {/* Y-axis labels — end 4px before the icon column (icon starts at padL-barH-2) */}
      {yTicks.map((tick, i) => (
        <text key={`yt-${i}`} x={padL - signalBarH - 4} y={yFor(tick) + 3} fontSize={9} fill={AXIS_TEXT} textAnchor="end">
          {tick}
        </text>
      ))}

      {/* Threshold reference lines (LowerRed + UpperRed only) */}
      {typeof thresholds?.UpperRed === 'number' && thresholds.UpperRed <= yMax && thresholds.UpperRed >= yMin && (
        <>
          <line
            x1={padL}
            y1={yFor(thresholds.UpperRed)}
            x2={padL + innerW}
            y2={yFor(thresholds.UpperRed)}
            stroke="#cbd5e1"
            strokeWidth={0.75}
            strokeDasharray="2,2"
          />
          <g transform={`translate(${padL + innerW + 2}, ${yFor(thresholds.UpperRed) - 7})`}>
            <rect width={36} height={14} rx={7} fill="white" stroke={ThresholdColors.Red} strokeWidth={1} />
            <text x={18} y={10} fontSize={9} fontWeight={600} fill={ThresholdColors.Red} textAnchor="middle">
              {`${Math.round(thresholds.UpperRed)}°`}
            </text>
          </g>
        </>
      )}
      {typeof thresholds?.LowerRed === 'number' && thresholds.LowerRed <= yMax && thresholds.LowerRed >= yMin && (
        <>
          <line
            x1={padL}
            y1={yFor(thresholds.LowerRed)}
            x2={padL + innerW}
            y2={yFor(thresholds.LowerRed)}
            stroke="#cbd5e1"
            strokeWidth={0.75}
            strokeDasharray="2,2"
          />
          <g transform={`translate(${padL + innerW + 2}, ${yFor(thresholds.LowerRed) - 7})`}>
            <rect width={36} height={14} rx={7} fill="white" stroke={ThresholdColors.Red} strokeWidth={1} />
            <text x={18} y={10} fontSize={9} fontWeight={600} fill={ThresholdColors.Red} textAnchor="middle">
              {`${Math.round(thresholds.LowerRed)}°`}
            </text>
          </g>
        </>
      )}

      {/* Temperature data line — stroked with the threshold gradient */}
      {dataPath ? (
        <path d={dataPath} fill="none" stroke={`url(#${gradientId})`} strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" clipPath={`url(#${clipId})`} />
      ) : null}

      {/* X-axis labels */}
      {visibleXTicks.map((tick, i) => (
        <text
          key={`xt-${i}`}
          x={xFor(tick)}
          y={padT + innerH + 12}
          fontSize={9}
          fill={AXIS_TEXT}
          textAnchor={i === 0 ? 'start' : i === visibleXTicks.length - 1 ? 'end' : 'middle'}
        >
          {xLabelFn(tick, i)}
        </text>
      ))}

      {/* Signal bars — fillRange=true: extends to range bounds, matching live chart */}
      {hasSignal && renderBarRects(signalDatasets, v => (v === 1 ? SIGNAL_ONLINE : SIGNAL_OFFLINE), signalY, signalBarH, true, 'sig')}
      {/* Signal bar border overlay */}
      {hasSignal && <rect x={padL} y={signalY} width={innerW} height={signalBarH} fill="none" stroke={BAR_BORDER} strokeWidth={0.1} />}
      {/* WiFi icon — right-justified just before the bar area (padL - barH - 2) */}
      {hasSignal && (
        <svg x={padL - signalBarH - 15} y={signalY} width={signalBarH} height={signalBarH} viewBox="0 0 18 18" fill="none">
          <path
            d="M9 12.625H9.0075M17.1048 4.52557C14.9697 2.56899 12.1242 1.375 8.99991 1.375C5.87561 1.375 3.03017 2.56899 0.89502 4.52557M3.54895 7.18223C5.00254 5.90174 6.91055 5.125 8.99998 5.125C11.0894 5.125 12.9974 5.90174 14.451 7.18223M11.7737 9.83133C11.0094 9.2322 10.0464 8.875 8.99991 8.875C7.93762 8.875 6.96129 9.24309 6.19153 9.85869"
            stroke={AXIS_TEXT}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {/* Door bars — fillRange=false: uses actual segment bounds, clipped to range */}
      {hasDoor && renderBarRects(doorDatasets, v => (v === 1 ? DOOR_CLOSED : DOOR_OPEN), doorY, doorBarH, false, 'door')}
      {/* Door bar border overlay */}
      {hasDoor && <rect x={padL} y={doorY} width={innerW} height={doorBarH} fill="none" stroke={BAR_BORDER} strokeWidth={0.1} />}
      {/* Door icon — right-justified just before the bar area (padL - barH - 2) */}
      {hasDoor && (
        <svg x={padL - doorBarH - 15} y={doorY} width={doorBarH} height={doorBarH} viewBox="0 0 18 18" fill="none">
          <path d="M8.98562 0.0675521C8.71485 -0.0469538 8.34088 -0.00640964 8.12065 0.118106C7.90045 0.242453 7.78991 0.392075 7.70552 0.533215C7.53675 0.815664 7.47485 1.0993 7.47485 1.41397V16.8308C7.47485 17.1428 7.5357 17.4271 7.70552 17.706C7.79043 17.8454 7.9062 17.9906 8.10915 18.1099C8.31209 18.2291 8.63552 18.2867 8.90488 18.2052L15.0572 16.3483C15.6167 16.1794 16.0317 15.5977 16.0317 14.9739V4.01148C16.0317 3.38212 15.5676 2.84675 15.138 2.66504L8.98562 0.0675521ZM9.03758 1.74495L14.4691 4.0451V14.9346L9.03758 16.5728V1.74495Z" fill={AXIS_TEXT} />
          <path d="M3.6108 2.59779C2.799 2.59779 2.25 3.30093 2.25 4.01719V14.2218C2.25 14.938 2.79904 15.6412 3.6108 15.6412H7.83832C8.26758 15.6397 8.61522 15.3014 8.61678 14.8838C8.61853 14.4639 8.26982 14.1223 7.83832 14.121H3.81255V4.11781H7.83832C8.26984 4.11645 8.61852 3.7748 8.61678 3.35496C8.61521 2.93713 8.26756 2.59905 7.83832 2.59753L3.6108 2.59779Z" fill={AXIS_TEXT} />
          <path d="M12.2916 9.12157C12.2916 9.80269 11.724 10.3548 11.0239 10.3548C10.3238 10.3548 9.75635 9.80264 9.75635 9.12157C9.75635 8.44049 10.3239 7.88831 11.0239 7.88831C11.7241 7.88831 12.2916 8.44049 12.2916 9.12157Z" fill={AXIS_TEXT} />
        </svg>
      )}
    </svg>
  );
};
